import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaUser, FaSpinner, FaSave, FaCalendarAlt, FaBuilding, FaTag, FaArrowLeft } from 'react-icons/fa';
import GooglePlaceAutocomplete from '../../components/shared/GooglePlaceAutocomplete';
import Input from '../../components/shared/Input';
import Select from '../../components/shared/Select';  // Assuming there's a styled Select component
import Button from '../../components/shared/Button';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import Checkbox from '../../components/shared/Checkbox';
import { getLeadById, createLead, updateLead, getLeadStatuses, getLeadOrigins, getLeadServices } from './leadService';
import DashboardLayout from '../../layouts/DashboardLayout';
import MaskedInput from '../../components/shared/MaskedInput';
import {
  FormContainer,
  FormSection,
  FormHeader,
  BackButton,
  FormGrid,
  FormField,
  InputWrapper,
  ErrorMessage,
  SubmitButton
} from '../../components/styled/FormComponents';

const FormWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background-color: ${(props) => props.theme.colors.surface};
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const LeadForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    zipcode: '',
    latitude: '',
    longitude: '',
    accept_sms: false,
    preferred_contact: '',
    LeadOriginId: '',
    LeadStatusId: '',
    service_name: '',
    when_ready: '',
    job_observation:'',
    serviceRequests: [{ Service: { service_name: '' } }],
  });
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [leadOrigins, setLeadOrigins] = useState([]);
  const [leadServices, setleadServices] = useState([]);

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams(); // Assuming you're using a route like /leads/:id

  // Fetch lead details if editing
  useEffect(() => {
    async function fetchLeadDetails() {
      if (id) {
        setLoading(true);
        const response = await getLeadById(id);
        const leadData = response.data;

        setFormData({
          ...leadData,
          LeadOriginId: leadData.origin?.id || '',  // Store the origin's ID
          serviceRequests: leadData.serviceRequests?.length
            ? leadData.serviceRequests
            : [{ Service: { service_name: '' } }],
        });
        setLoading(false);
      }
    }
    fetchLeadDetails();
  }, [id]);

  // Fetch lead statuses and origins
  useEffect(() => {
    async function fetchData() {
      const statuses = await getLeadStatuses();
      const origins = await getLeadOrigins();
      const services = await getLeadServices();
      setLeadStatuses(statuses.data);
      setLeadOrigins(origins.data);
      setleadServices(services.data);
    }
    fetchData();
  }, []);

  const handleLeadStatus = async (e) => {
    setFormData({ ...formData, LeadStatusId: e.target.value });
  }

  const handleLeadOrigin = (e) => {
    const selectedOrigin = leadOrigins.find((origin) => origin.id === parseInt(e.target.value, 10));
    setFormData({ ...formData, LeadOriginId: e.target.value, origin_name: selectedOrigin.origin_name });  // Store the ID
  };

  const handleLeadServices = (e) => {
    // Make sure serviceRequests is initialized as an array
    const updatedServiceRequests = formData.serviceRequests.length > 0
      ? [...formData.serviceRequests]
      : [{ Service: { service_name: '' } }];
    
    // Update the service_name within the first service request's Service object
    updatedServiceRequests[0].Service.service_name = e.target.value;

    setFormData({
      ...formData,
      serviceRequests: updatedServiceRequests,
      service_requested: e.target.value
    });
  };

  const handleWhenReady = (e) => {
    setFormData({ ...formData, when_ready: e.target.value });
  };

   // Handle Google Autocomplete address selection, keeping the existing form data intact
   const handleAddressSelect = (address, lat, lng, place) => {
    setFormData((prevData) => ({
      ...prevData,
      street: address,
      city: place.address_components.find((c) => c.types.includes('locality'))?.long_name || prevData.city,
      state: place.address_components.find((c) => c.types.includes('administrative_area_level_1'))?.short_name || prevData.state,
      zipcode: place.address_components.find((c) => c.types.includes('postal_code'))?.long_name || prevData.zipcode,
      latitude: lat,
      longitude: lng,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (id) {
        await updateLead(id, formData);
      } else {
        await createLead(formData);
      }
      navigate('/leads');
    } catch (error) {
      console.error('Error saving lead:', error);
    }
    setLoading(false);
  };

  return (
    <DashboardLayout>
      <FormContainer>
        <FormHeader>
          <BackButton onClick={() => navigate('/leads')}>
            <FaArrowLeft /> Back to Leads
          </BackButton>
          <h2>{id ? 'Edit Lead' : 'New Lead'}</h2>
        </FormHeader>

        {loading ? (
          <FormSection>
            <LoadingSpinner />
          </FormSection>
        ) : (
          <form onSubmit={handleSubmit}>
            <FormSection>
              <h3>Basic Information</h3>
              <FormGrid>
                <FormField index={0}>
                  <Input
                    label="Name"
                    icon={<FaUser />}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter full name"
                    required
                  />
                </FormField>

                <FormField index={1}>
                  <Input
                    label="Email"
                    icon={<FaEnvelope />}
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter email address"
                    required
                  />
                </FormField>

                <FormField index={2}>
                  <MaskedInput
                    label="Phone"
                    mask="+19999999999"
                    icon={<FaPhoneAlt />}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Enter phone number"
                    required
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormSection>
              <h3>Address Information</h3>
              <FormField index={3}>
                <GooglePlaceAutocomplete
                  initialValue={formData.street}
                  placeholder="Start typing address"
                  required
                  onSelect={handleAddressSelect}
                />
              </FormField>

              <FormGrid>
                <FormField index={4}>
                  <Input
                    label="City"
                    icon={<FaMapMarkerAlt />}
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="City"
                    required
                  />
                </FormField>

                <FormField index={5}>
                  <Input
                    label="State"
                    icon={<FaMapMarkerAlt />}
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="State"
                    required
                  />
                </FormField>

                <FormField index={6}>
                  <MaskedInput
                    label="Zipcode"
                    mask="99999"
                    icon={<FaMapMarkerAlt />}
                    value={formData.zipcode}
                    onChange={(e) => setFormData({ ...formData, zipcode: e.target.value })}
                    placeholder="Zipcode"
                    required
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormSection>
              <h3>Lead Details</h3>
              <FormGrid>
                <FormField index={7}>
                  <Select
                    label="Lead Origin"
                    icon={<FaTag />}
                    value={formData.LeadOriginId}
                    required
                    disabled={id}
                    onChange={handleLeadOrigin}
                  >
                    <option value="">Select Lead Origin</option>
                    {leadOrigins.map((origin) => (
                      <option key={origin.id} value={origin.id}>
                        {origin.origin_name}
                      </option>
                    ))}
                  </Select>
                </FormField>

                <FormField index={8}>
                  <Select
                    label="Service Requested"
                    icon={<FaBuilding />}
                    value={formData.serviceRequests?.[0]?.Service?.service_name || ''}
                    required
                    disabled={id}
                    onChange={handleLeadServices}
                  >
                    <option value="">Select Service</option>
                    {leadServices.map((service) => (
                      <option key={service.service_name} value={service.service_name}>
                        {service.service_name}
                      </option>
                    ))}
                  </Select>
                </FormField>

                <FormField index={9}>
                  <Select
                    label="Preferred Contact"
                    icon={<FaPhoneAlt />}
                    value={formData.preferred_contact || ''}
                    onChange={(e) => setFormData({ ...formData, preferred_contact: e.target.value })}
                  >
                    <option value="">Not specified</option>
                    <option value="text">Text Message</option>
                    <option value="call">Phone Call</option>
                    <option value="email">Email</option>
                  </Select>
                </FormField>

                <FormField index={10}>
                  <Select
                    label="When Ready"
                    icon={<FaCalendarAlt />}
                    disabled={id}
                    value={formData.when_ready}
                    onChange={handleWhenReady}
                    required
                  >
                    <option value="">Select readiness</option>
                    <option value="ASAP">ASAP</option>
                    <option value="In a Month">In a Month</option>
                    <option value="In two Months">In two Months</option>
                    <option value="Just Looking for Prices">Just Looking for Prices</option>
                  </Select>
                </FormField>
              </FormGrid>

              <FormField index={11}>
                <Checkbox
                  label="Accept SMS"
                  checked={formData.accept_sms}
                  onChange={() => setFormData({ ...formData, accept_sms: !formData.accept_sms })}
                />
              </FormField>
            </FormSection>

            <SubmitButton type="submit" disabled={loading}>
              {loading ? <FaSpinner className="spin" /> : <FaSave />}
              {id ? 'Update Lead' : 'Create Lead'}
            </SubmitButton>
          </form>
        )}
      </FormContainer>
    </DashboardLayout>
  );
};

export default LeadForm;

  // return (
  //   <DashboardLayout>
  //     <FormWrapper>
  //       <h2>{id ? 'Edit Lead' : 'Add Lead'}</h2>

  //       {loading && <LoadingSpinner />}

  //       {!loading && (
  //         <form onSubmit={handleSubmit}>
  //           <Input
  //             label="Name"
  //             icon={<FaUser />}
  //             value={formData.name}
  //             onChange={(e) => setFormData({ ...formData, name: e.target.value })}
  //             placeholder="Digit the name"
  //             required
  //           />
  //           <Input
  //             label="Email"
  //             icon={<FaEnvelope />}
  //             type="email"
  //             value={formData.email}
  //             onChange={(e) => setFormData({ ...formData, email: e.target.value })}
  //             placeholder="Valid e-mail"
  //             required
  //           />
  //           <MaskedInput
  //             label="Phone"
  //             mask="+19999999999"
  //             icon={<FaPhoneAlt />}
  //             value={formData.phone}
  //             onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
  //             placeholder="Mobile Phone number"
  //             required
  //           />

  //           {/* <GooglePlaceAutocomplete
  //             initialValue={formData.street}
  //             placeholder="Start typing"
  //             required
  //             onSelect={(address, lat, lng, place) => {
  //               setFormData({
  //                 ...formData,
  //                 street: address,
  //                 city: place.address_components.find((c) => c.types.includes('locality'))?.long_name || '',
  //                 state: place.address_components.find((c) => c.types.includes('administrative_area_level_1'))?.short_name || '',
  //                 zipcode: place.address_components.find((c) => c.types.includes('postal_code'))?.long_name || '',
  //                 latitude: lat,
  //                 longitude: lng,
  //               });
  //             }}
  //           /> */}

  //           <GooglePlaceAutocomplete
  //             initialValue={formData.street}
  //             placeholder="Start typing address"
  //             required
  //             onSelect={handleAddressSelect}
  //           />

  //           <Input
  //             label="City"
  //             icon={<FaMapMarkerAlt />}
  //             value={formData.city}
  //             onChange={(e) => setFormData({ ...formData, city: e.target.value })}
  //             placeholder="City"
  //             required
  //           />
  //           <Input
  //             label="State"
  //             icon={<FaMapMarkerAlt />}
  //             value={formData.state}
  //             onChange={(e) => setFormData({ ...formData, state: e.target.value })}
  //             placeholder="State"
  //             required
  //           />
  //           <MaskedInput
  //             label="Zipcode"
  //             mask="99999"
  //             icon={<FaMapMarkerAlt />}
  //             value={formData.zipcode}
  //             onChange={(e) => setFormData({ ...formData, zipcode: e.target.value })}
  //             placeholder="Zipcode"
  //             required
  //           />           

  //           <Select
  //             label="Lead Origin"
  //             value={formData.LeadOriginId}
  //             required
  //             disabled={id ? "disabled" : ''}
  //             onChange={handleLeadOrigin}
  //           >
  //             <option value="">Select Lead Origin</option>
  //             {leadOrigins.map((origin) => (
  //               <option key={origin.id} value={origin.id}>
  //                 {origin.origin_name}
  //               </option>
  //             ))}
  //           </Select>

  //           <Select
  //             label="Service Requested"
  //             value={formData.serviceRequests?.[0]?.Service?.service_name || ''}
  //             required
  //             disabled={id ? "disabled" : ''}
  //             onChange={handleLeadServices}
  //           >
  //             <option value="">Select Service</option>
  //             {leadServices.map((service) => (
  //               <option key={service.service_name} value={service.service_name}>
  //                 {service.service_name}
  //               </option>
  //             ))}
  //           </Select>

  //           <Select
  //             label="When Ready"
  //             disabled={id ? "disabled" : ""}
  //             value={formData.when_ready}
  //             onChange={handleWhenReady}
  //             required
  //           >
  //             <option value="">Select readiness</option>
  //             <option value="ASAP">ASAP</option>
  //             <option value="In a Month">In a Month</option>
  //             <option value="In two Months">In two Months</option>
  //             <option value="Just Looking for Prices">Just Looking for Prices</option>
  //           </Select>

  //           <Checkbox
  //             label="Accept SMS"
  //             checked={formData.accept_sms}
  //             onChange={() => setFormData({ ...formData, accept_sms: !formData.accept_sms })}
  //           />

  //           <Button type="submit">{id ? 'Update Lead' : 'Add Lead'}</Button>
  //         </form>
  //       )}
  //     </FormWrapper>
  //   </DashboardLayout>
  // );
// };

// export default LeadForm;
